/**
 * Closeout "Hand to…" (threads/hand-it-off; mockup
 * https://claude.ai/artifact/Mu9v1tYqoejKiYU9qyzeB9).
 *
 * When the office can't move a job on its own ("Customer wants Net 30, OK?")
 * it hands the job to a person with one line. The job goes to the top of that
 * person's Yours pile. They answer Done or Hand back, and the job comes back
 * to the sender with the answer on the card until the sender opens it.
 * No task, no bell.
 *
 *   HandToButton  — "Hand to ▾" (Closeout header, job page).
 *   HandToDialog  — pick a person, one required line.
 *   HandoffBanner — the ask / the answer, with Done · Hand back · Take it back.
 *   JobHandoffStrip — the job page's version (only for jobs in Closeout).
 *
 * Visual first: pink = with someone, green = an answer came back.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeftToLine, ArrowRightToLine, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    HANDOFF_COLOR,
    HANDOFF_REPLY_COLOR,
    handoffReplyLine,
    handoffReturnLine,
    useWhoseTurn,
    type AskOption,
    type Ball,
    type JobNote,
    type BallHandoff,
    type HandoffPerson,
} from "@/lib/closeout-ball";
import { refreshCloseOutQueue } from "@/pages/close-out/queueRefresh";
import { JobNotesDialog, WhyStrip } from "@/pages/close-out/ball";
import { DoneSendBack } from "@/components/closeout/whose-turn";

export const HANDOFF_REASON_MAX = 300;

interface JobHandoffResponse {
    open: BallHandoff | null;
    reply: BallHandoff | null;
    people: HandoffPerson[];
}

export function useJobHandoff(jobId: string, enabled = true) {
    return useQuery({
        queryKey: ["close-out-handoff", jobId],
        queryFn: () => api.get<JobHandoffResponse>(`/api/close-out/jobs/${jobId}/handoff`),
        enabled: enabled && !!jobId,
        staleTime: 30_000,
        retry: false,
    });
}

function useRefresh(jobId: string) {
    const qc = useQueryClient();
    return () => {
        refreshCloseOutQueue(qc, { jobId });
        qc.invalidateQueries({ queryKey: ["close-out-handoff", jobId] });
        qc.invalidateQueries({ queryKey: ["close-out-ball-for-task", jobId] });
        qc.invalidateQueries({ queryKey: ["job-handoff-queue", jobId] });
    };
}

function errMsg(e: any, fallback: string): string {
    return e?.body?.message ?? fallback;
}

// ─── Button + dialog ────────────────────────────────────────────────────
export function HandToButton({
    jobId,
    jobName,
    disabled,
    className,
    asks,
}: {
    jobId: string;
    jobName: string;
    /** An open hand-off already sits on the job. */
    disabled?: boolean;
    className?: string;
    /** Whose turn: the job's open steps to pick from (else one typed line). */
    asks?: AskOption[];
}) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button
                size="sm"
                variant="outline"
                disabled={disabled}
                onClick={() => setOpen(true)}
                className={cn(
                    "border-pink-300 bg-pink-50 text-pink-800 hover:bg-pink-100 dark:border-pink-800 dark:bg-pink-950/40 dark:text-pink-200",
                    className,
                )}
                title={disabled ? "This job is already with someone." : "Hand this job to a person"}
                data-testid="hand-to-button"
            >
                <ArrowRightToLine className="mr-1 h-3.5 w-3.5" />
                Hand to ▾
            </Button>
            {open && <HandToDialog jobId={jobId} jobName={jobName} asks={asks} onClose={() => setOpen(false)} />}
        </>
    );
}

export function HandToDialog({
    jobId,
    jobName,
    defaultToUserId,
    onClose,
    onDone,
    asks,
}: {
    jobId: string;
    jobName: string;
    defaultToUserId?: string | null;
    /** Whose turn (10/28): pick what you need from the job's open steps.
     *  GWC watches that step and sends the job back when it clears. */
    asks?: AskOption[];
    onClose: () => void;
    /** Called after it is handed off (the Ask someone dialog closes too). */
    onDone?: () => void;
}) {
    const q = useJobHandoff(jobId);
    const refresh = useRefresh(jobId);
    const people = q.data?.people ?? [];
    const [toId, setToId] = useState<string | null>(defaultToUserId ?? null);
    const [reason, setReason] = useState("");
    const steps = asks && asks.length > 0 ? asks : null;
    const [askKind, setAskKind] = useState<string | null>(null);
    useEffect(() => {
        if (defaultToUserId) setToId(defaultToUserId);
    }, [defaultToUserId]);
    const to = people.find((p) => p.id === toId) ?? null;
    // A picked step needs no words; "Something else" (or the old dialog) does.
    const watched = !!steps && !!askKind && askKind !== "other";
    const canSend = !!toId && (steps ? !!askKind && (watched || !!reason.trim()) : !!reason.trim());
    const m = useMutation({
        mutationFn: () =>
            api.post(`/api/close-out/jobs/${jobId}/handoff`, {
                toUserId: toId,
                reason: reason.trim(),
                ...(steps && askKind ? { askKind } : {}),
            }),
        onSuccess: () => {
            toast.success(
                watched
                    ? `Handed to ${to?.first_name ?? "them"}. When it's done, it comes back to you by itself.`
                    : `Handed to ${to?.first_name ?? "them"}. It's at the top of their pile.`,
            );
            refresh();
            onDone?.();
            onClose();
        },
        onError: (e: any) => toast.error(errMsg(e, "Couldn't hand it off. Try again.")),
    });
    const already = q.data?.open ?? null;
    return (
        <Dialog open onClose={onClose} title={`Hand this job to… · ${jobName}`}>
            <div className="space-y-3" data-testid="hand-to-dialog">
                {already ? (
                    <p className="text-sm">
                        This job is already with <b>{already.toName}</b>: “{already.reason}”
                    </p>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-1.5">
                            {people.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => setToId(p.id)}
                                    aria-pressed={toId === p.id}
                                    className={cn(
                                        "inline-flex items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-[13px] font-bold",
                                        toId === p.id
                                            ? "border-pink-600 bg-pink-50 text-pink-800 dark:bg-pink-950/50 dark:text-pink-200"
                                            : "border-border hover:bg-muted",
                                    )}
                                >
                                    <span
                                        className={cn(
                                            "flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-extrabold",
                                            toId === p.id ? "bg-pink-700 text-white" : "bg-muted text-foreground/60",
                                        )}
                                    >
                                        {(p.first_name ?? "?").charAt(0)}
                                    </span>
                                    {p.first_name}
                                </button>
                            ))}
                            {q.isLoading && <span className="text-sm text-muted-foreground">Loading people…</span>}
                        </div>
                        {steps && (
                            <div data-testid="hand-to-asks">
                                <div className="mb-1.5 text-[12.5px] font-bold">
                                    What do you need{to ? ` from ${to.first_name}` : ""}?
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {steps.map((a) => (
                                        <button
                                            key={a.kind}
                                            type="button"
                                            onClick={() => setAskKind(a.kind)}
                                            aria-pressed={askKind === a.kind}
                                            data-testid={`hand-to-ask-${a.kind}`}
                                            className={cn(
                                                "rounded-full border px-3 py-1 text-[12.5px] font-bold",
                                                askKind === a.kind
                                                    ? "border-pink-600 bg-pink-50 text-pink-800 dark:bg-pink-950/50 dark:text-pink-200"
                                                    : a.kind === "other"
                                                      ? "border-dashed border-border text-foreground/75 hover:bg-muted"
                                                      : "border-border hover:bg-muted",
                                            )}
                                        >
                                            {a.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(!steps || askKind) && (
                            <Input
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                maxLength={HANDOFF_REASON_MAX}
                                placeholder={
                                    watched
                                        ? "Anything to add? (not needed)"
                                        : "One line. e.g. Is the $85 trip fee right?"
                                }
                                aria-label="What you need from them"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && canSend && !m.isPending) m.mutate();
                                }}
                            />
                        )}
                        <p className="text-[12px] text-muted-foreground">
                            {steps
                                ? watched
                                    ? "GWC watches for it. When it's done, the job comes back to you by itself."
                                    : askKind === "other"
                                      ? `${to?.first_name ?? "They"} taps Done when it's handled, and it comes back to you.`
                                      : "Pick one. Nobody gets a bell or a task."
                                : `Say what you need${to ? ` from ${to.first_name}` : ""}. It goes to the top of their pile. Nobody gets a bell or a task.`}
                        </p>
                    </>
                )}
                <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={onClose}>
                        Cancel
                    </Button>
                    {!already && (
                        <Button
                            size="sm"
                            disabled={!canSend || m.isPending}
                            onClick={() => m.mutate()}
                            className="bg-pink-700 text-white hover:bg-pink-800"
                        >
                            <ArrowRightToLine className="mr-1 h-3.5 w-3.5" />
                            {to ? `Hand to ${to.first_name}` : "Hand it off"}
                        </Button>
                    )}
                </div>
            </div>
        </Dialog>
    );
}

// ─── The ask / the answer on the job ────────────────────────────────────
function timeLabel(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return d.toDateString() === new Date().toDateString()
        ? `today at ${time}`
        : `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} at ${time}`;
}

export function HandoffBanner({
    jobId,
    handoff,
    viewerId,
    onGotIt,
}: {
    jobId: string;
    handoff: BallHandoff;
    viewerId: string | null;
    /** Answered: hides it for good (the job page; Closeout marks it on open). */
    onGotIt?: () => void;
}) {
    const refresh = useRefresh(jobId);
    const turnOn = useWhoseTurn().on;
    const [mode, setMode] = useState<null | "done" | "handed_back">(null);
    const [note, setNote] = useState("");
    const answer = useMutation({
        mutationFn: (outcome: "done" | "handed_back") =>
            api.post(`/api/close-out/handoffs/${handoff.id}/answer`, { outcome, note: note.trim() }),
        onSuccess: (_r, outcome) => {
            toast.success(
                outcome === "done" ? `Done. ${handoff.fromName} will see it.` : `Back with ${handoff.fromName}.`,
            );
            setMode(null);
            setNote("");
            refresh();
        },
        onError: (e: any) => toast.error(errMsg(e, "Couldn't save that. Try again.")),
    });
    const takeBack = useMutation({
        mutationFn: () => api.post(`/api/close-out/handoffs/${handoff.id}/take-back`, {}),
        onSuccess: () => {
            toast.success("Taken back. It's yours again.");
            refresh();
        },
        onError: (e: any) => toast.error(errMsg(e, "Couldn't take it back. Try again.")),
    });

    if (handoff.state === "answered" && handoff.doneLine && !(handoff.reply ?? "").trim()) {
        // Whose turn: "Ready for Darcy: Corbin approved the quote, 2:14 PM".
        return (
            <div
                className="flex flex-wrap items-center gap-2.5 border-b border-border bg-emerald-50 px-3.5 py-2 text-[13px] dark:bg-emerald-950/40"
                data-testid="handoff-answer"
            >
                <span
                    className="inline-flex items-center gap-1.5 font-extrabold"
                    style={{ color: HANDOFF_REPLY_COLOR }}
                >
                    <ArrowLeftToLine className="h-4 w-4" />
                    {handoffReturnLine(handoff)}
                </span>
                <span className="text-[11.5px] text-muted-foreground">Asked: {handoff.reason}</span>
                {onGotIt && (
                    <Button size="sm" variant="outline" className="ml-auto h-7" onClick={onGotIt}>
                        Got it
                    </Button>
                )}
            </div>
        );
    }
    if (handoff.state === "answered") {
        return (
            <div
                className="flex flex-wrap items-center gap-2.5 border-b border-border bg-emerald-50 px-3.5 py-2 text-[13px] dark:bg-emerald-950/40"
                data-testid="handoff-answer"
            >
                <span
                    className="inline-flex items-center gap-1.5 font-extrabold"
                    style={{ color: HANDOFF_REPLY_COLOR }}
                >
                    <ArrowLeftToLine className="h-4 w-4" />
                    {handoff.replyByName || handoff.toName}{" "}
                    {handoff.outcome === "handed_back" ? "handed it back" : "answered"}
                </span>
                <span className="font-bold">“{handoffReplyLine(handoff).replace(/^[^:]*:\s*/, "")}”</span>
                <span className="text-[11.5px] text-muted-foreground">
                    {viewerId && viewerId === handoff.fromUserId ? "You asked" : `${handoff.fromName} asked`}:{" "}
                    {handoff.reason}
                </span>
                {onGotIt && (
                    <Button size="sm" variant="outline" className="ml-auto h-7" onClick={onGotIt}>
                        Got it
                    </Button>
                )}
            </div>
        );
    }

    const iAmTo = !!viewerId && viewerId === handoff.toUserId;
    const iAmFrom = !!viewerId && viewerId === handoff.fromUserId;
    return (
        <div className="border-b border-border" data-testid="handoff-banner">
            <div className="flex flex-wrap items-center gap-2.5 bg-pink-50 px-3.5 py-2 text-[13px] dark:bg-pink-950/40">
                <span className="inline-flex items-center gap-1.5 font-extrabold" style={{ color: HANDOFF_COLOR }}>
                    <ArrowRightToLine className="h-4 w-4" />
                    {iAmTo ? `Hand-off from ${handoff.fromName}` : `With ${handoff.toName}`}
                </span>
                <span className="font-bold">“{handoff.reason}”</span>
                <span className="text-[11.5px] text-muted-foreground">
                    {iAmTo
                        ? timeLabel(handoff.at)
                        : iAmFrom
                          ? `you handed it ${timeLabel(handoff.at)}`
                          : `${handoff.fromName} handed it ${timeLabel(handoff.at)}`}
                </span>
                {iAmFrom && !iAmTo && (
                    <Button
                        size="sm"
                        variant="outline"
                        className="ml-auto h-7"
                        disabled={takeBack.isPending}
                        onClick={() => takeBack.mutate()}
                        data-testid="handoff-take-back"
                    >
                        Take it back
                    </Button>
                )}
            </div>
            {iAmTo && turnOn && (
                // Whose turn (10/28): one tap, or nothing at all when GWC is
                // watching the step. Hand back with a note stays one link away.
                <div
                    className="flex flex-wrap items-center gap-2.5 bg-pink-50/40 px-3.5 py-2 text-[12.5px] dark:bg-pink-950/20"
                    data-testid="handoff-turn-actions"
                >
                    {handoff.askKind && handoff.askKind !== "other" ? (
                        <span className="font-semibold text-foreground/75">
                            GWC watches for it. When it&apos;s done, it goes back to {handoff.fromName} by itself.
                        </span>
                    ) : (
                        <DoneSendBack jobId={jobId} handoff={handoff} viewerId={viewerId} />
                    )}
                    <button
                        type="button"
                        onClick={() => setMode(mode === "handed_back" ? null : "handed_back")}
                        className="ml-auto text-[12px] font-bold text-foreground/60 underline-offset-2 hover:underline"
                        data-testid="handoff-hand-back"
                    >
                        Hand back with a note
                    </button>
                </div>
            )}
            {iAmTo && (!turnOn || mode === "handed_back") && (
                <div className="grid gap-2 bg-pink-50/40 px-3.5 py-2.5 dark:bg-pink-950/20">
                    <div className={cn("flex flex-wrap gap-2", turnOn && "hidden")}>
                        <AnswerChoice
                            on={mode === "done"}
                            onClick={() => setMode("done")}
                            icon={<Check className="h-4 w-4" />}
                            label="Done"
                            sub="I handled it"
                            testId="handoff-done"
                        />
                        <AnswerChoice
                            on={mode === "handed_back"}
                            onClick={() => setMode("handed_back")}
                            icon={<ArrowLeftToLine className="h-4 w-4" />}
                            label={`Hand back to ${handoff.fromName}`}
                            sub="with a note"
                            testId="handoff-hand-back"
                        />
                    </div>
                    {mode && (
                        <>
                            <Textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={2}
                                maxLength={1000}
                                autoFocus
                                placeholder={
                                    mode === "done"
                                        ? "Anything to tell them? (not required)"
                                        : "What should they do now?"
                                }
                                aria-label="Your answer"
                            />
                            <div className="flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => setMode(null)}>
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    disabled={answer.isPending || (mode === "handed_back" && !note.trim())}
                                    onClick={() => answer.mutate(mode)}
                                >
                                    {mode === "done" ? "Mark done" : `Hand back to ${handoff.fromName}`}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function AnswerChoice({
    on,
    onClick,
    icon,
    label,
    sub,
    testId,
}: {
    on: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    sub: string;
    testId: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={on}
            data-testid={testId}
            className={cn(
                "inline-flex items-center gap-2 rounded-lg border-[1.5px] bg-card px-3 py-2 text-[13px] font-extrabold",
                on
                    ? "border-emerald-700 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200"
                    : "border-border hover:bg-muted",
            )}
        >
            {icon}
            {label}
            <span className="text-[11px] font-medium text-muted-foreground">{sub}</span>
        </button>
    );
}

// ─── Job page ───────────────────────────────────────────────────────────
/** The job page's hand-off strip. Shows only for jobs in Closeout. */
export function JobHandoffStrip({ jobId, jobName }: { jobId: string; jobName: string }) {
    const { isOffice, user } = useAuth();
    const viewerId: string | null = user?.id ?? null;
    const qc = useQueryClient();
    const inQueue = useQuery({
        queryKey: ["job-handoff-queue", jobId],
        queryFn: () =>
            api.get<{
                jobs: Array<{
                    id: string;
                    ball?: Ball | null;
                    notes?: JobNote[];
                    packet_sent_at?: string | null;
                    asks?: AskOption[];
                }>;
            }>(`/api/close-out/queue?jobId=${encodeURIComponent(jobId)}`),
        enabled: isOffice && !!jobId,
        retry: false,
        staleTime: 30_000,
    });
    const h = useJobHandoff(jobId, isOffice && (inQueue.data?.jobs?.length ?? 0) > 0);
    const seen = useMutation({
        mutationFn: (id: string) => api.post(`/api/close-out/handoffs/${id}/seen`, {}),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["close-out-handoff", jobId] });
            refreshCloseOutQueue(qc, { jobId });
        },
    });
    const [notesOpen, setNotesOpen] = useState(false);
    if (!isOffice || (inQueue.data?.jobs?.length ?? 0) === 0) return null;
    const open = h.data?.open ?? null;
    const reply = h.data?.reply ?? null;
    const row = inQueue.data!.jobs[0]!;
    return (
        <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid="job-handoff-strip">
            {/* "Why isn't this out yet?" — the same line Closeout shows (10/16). */}
            <WhyStrip job={row} onNotes={() => setNotesOpen(true)} />
            {notesOpen && <JobNotesDialog jobId={jobId} jobName={jobName} onClose={() => setNotesOpen(false)} />}
            {open && <HandoffBanner jobId={jobId} handoff={open} viewerId={viewerId} />}
            {!open && reply && (
                <HandoffBanner
                    jobId={jobId}
                    handoff={reply}
                    viewerId={viewerId}
                    onGotIt={reply.fromUserId === viewerId ? () => seen.mutate(reply.id) : undefined}
                />
            )}
            {!open && (
                <div className="flex flex-wrap items-center gap-2 px-3.5 py-2 text-[12.5px]">
                    <span className="text-muted-foreground">Stuck on something only someone else can answer?</span>
                    <HandToButton jobId={jobId} jobName={jobName} className="ml-auto" asks={row.asks} />
                </div>
            )}
        </div>
    );
}
